import { Tab, Tabs } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type HospitalDataContextType, useHospitalData } from "../../contexts/HospitalDataProvider";

export type ViewType = "Procedure Time" | "By Admission" | "By Surgeon" | "By Procedure";
export const ViewTypeOptions: ViewType[] = ["Procedure Time", "By Admission", "By Surgeon", "By Procedure"];

// The internal ViewType values keep "By " (used for logic + localStorage); only the visible
// label strips it, so the tabs read Procedure Time / Admission / Surgeon / Procedure.
const viewTypeLabel = (viewType: ViewType): string => viewType.replace(/^By /, "");

interface ProceduresViewSelectorProps {
	onViewChanged?: (viewType: ViewType) => void;
}

// Falls back to the default view when localStorage holds an unknown value.
const readStoredViewType = (): ViewType => {
	const stored = localStorage.getItem("proceduresView");
	return stored && ViewTypeOptions.includes(stored as ViewType) ? (stored as ViewType) : ViewTypeOptions[0];
};

const ProceduresViewSelector: React.FC<ProceduresViewSelectorProps> = ({ onViewChanged }) => {
	const [selectedViewType, setSelectedViewType] = useState<ViewType>(readStoredViewType);
	const { t } = useTranslation();
	const { reorderNurses }: HospitalDataContextType = useHospitalData();

	const handleChange = useCallback(
		(_event: React.SyntheticEvent, newValue: ViewType) => {
			setSelectedViewType(newValue);
			localStorage.setItem("proceduresView", newValue);
			onViewChanged?.(newValue);
			reorderNurses();
		},
		[onViewChanged, reorderNurses],
	);

	useEffect(() => {
		onViewChanged?.(selectedViewType);
	}, []);

	return (
		<Tabs value={selectedViewType} onChange={handleChange} variant="fullWidth" aria-label="view type" sx={{ minHeight: "40px" }}>
			{ViewTypeOptions.map((value) => (
				<Tab
					key={value}
					value={value}
					label={t(viewTypeLabel(value))}
					sx={{ textTransform: "none", fontSize: "0.875rem", minHeight: "40px", padding: "8px 12px", whiteSpace: "nowrap" }}
				/>
			))}
		</Tabs>
	);
};

export default ProceduresViewSelector;
